import { fieldSchemaMessages } from "@thalamos/common";
import type { AssessmentActivityKind } from "@thalamos/haven-common";

import {
  PRODUCT_ATTRIBUTION,
  PRODUCT_NAME,
  PRODUCT_STRAPLINE,
} from "../../product.js";

/**
 * Every user-facing string belongs here rather than inline in a component. The
 * words for the people being assessed differ from eMHA's ("person", not
 * "patient"), and keeping the vocabulary in one place is what makes changing it
 * a single edit rather than a sweep through the components.
 */
const common = {
  // Wording for the shared field schemas' validation messages. Spread rather
  // than restated so a schema whose message key is new cannot render as a raw
  // key here.
  fieldSchema: fieldSchemaMessages,

  app: {
    // The product name lives in one module because the static shell needs it at
    // build time too; see `src/product.ts`.
    name: PRODUCT_NAME,
    // Product-level, deliberately naming no instrument: CANS is the first
    // assessment this will carry but is not expected to be the only one.
    strapline: PRODUCT_STRAPLINE,
    // Byline form. The "by" is translatable prose, so it is composed here
    // rather than concatenated onto the name in a component.
    attribution: `by ${PRODUCT_ATTRIBUTION}`,
  },

  // The deployment's word for a person, the subject of a record. Grouped so
  // the vocabulary can be reviewed as a set.
  person: {
    singular: "young person",
    plural: "young people",
  },

  auth: {
    signIn: "Sign in",
    signOut: "Sign out",
    signedInAs: "Signed in as",
    signingIn: "Signing you in",
    signInFailed:
      "We could not sign you in. Try again, and if it keeps happening, ask whoever manages your account.",
  },

  sessionContextSelector: {
    title: "Choose how to sign in",
    description:
      "You can act in more than one capacity. What you can see and do depends on which you choose.",
    label: "Team or organisation",
    continue: "Continue",
  },

  // The application shell's session context switcher.
  layout: {
    switcher: {
      // "Working as" rather than "Role": a context is a kind of work at an
      // organisation, and each row names both halves.
      heading: "Working as",
      // Both halves, or the same kind of work at two organisations reads out
      // alike. Interpolated so their place in the sentence stays the
      // translator's.
      open: "Change what you are working as — {{kindOfWork}} at {{organisation}}",
      change: "Change",
    },
  },

  home: {
    title: "Home",
    noAssessments: "No assessments yet.",
  },

  approvals: {
    title: "Awaiting your decision",
    description:
      "Definitive assessments submitted to you for approval, longest waiting first.",
    none: "Nothing is waiting for your decision.",
    person: "Young person",
    assessor: "Assessor",
    instrument: "Instrument version",
    submitted: "Submitted",
    reviewFor: "Review {{personName}}'s assessment",
  },

  assessment: {
    // Messages the shared assessment schemas refuse a request with, keyed as
    // those schemas name them.
    deletion: {
      reasonRequired: "Say why this assessment is being deleted.",
      reasonTooLong: "A reason can be at most 4000 characters.",
    },
    // An approver's decision on a submission. The reason messages are keyed as
    // the shared decision schema names them.
    decision: {
      title: "Your decision",
      explanation:
        "Your decision covers the whole assessment. You can't approve part of it, and you can't change a rating. Returning it sends all of it back to {{assessor}} with your reason.",
      approve: "Approve",
      return: "Return with a reason",
      approveDialog: {
        title: "Approve this assessment?",
        body: "This will become {{personName}}'s definitive assessment. Once approved, it can't be changed or returned.",
        confirm: "Approve",
      },
      returnDialog: {
        title: "Return this assessment?",
        intro: "{{assessor}} will get the whole assessment back to work on.",
        reason: "Why are you returning it?",
        reasonHelp:
          "Required. {{assessor}} will see this, and it stays in the assessment history. Name the domains or items you want them to look at again.",
        confirm: "Return assessment",
      },
      changed:
        "This assessment changed while you were deciding, so your decision was not recorded.",
      failed: "Your decision could not be recorded. Try again.",
      reasonRequired: "Say why you are returning it.",
      reasonTooLong: "A reason can be at most 4000 characters.",
    },
    // Sending a definitive assessment for approval.
    submitDialog: {
      title: "Submit for approval?",
      goesTo: "This assessment will go to {{approver}} for approval.",
      next: "Once submitted, you can't change it or take it back. {{approver}} will approve it or return it to you.",
      confirm: "Submit for approval",
      approverChanged: "The Approver for this assessment has changed.",
    },
    approverNeeded: {
      named:
        "This assessment needs an Approver before it can be submitted. {{caseManager}} can add one.",
      unnamed: "This assessment needs an Approver before it can be submitted.",
    },
    // A submission its approver returned, as its assessor reads it.
    returned: {
      title: "Returned for changes",
      by: "Returned by {{approver}} on {{date}}",
      on: "Returned on {{date}}",
      continue: "Continue",
      reopenHelp:
        "Your answers from this submission are kept. Change what needs another look, then submit it again.",
      changed:
        "This assessment changed before it could be reopened. Reload the page to see where it stands.",
      failed: "This assessment could not be reopened. Try again.",
    },
    // Completing an assessment that is not definitive, which nobody approves.
    completeDialog: {
      title: "Complete this assessment?",
      body: "Once completed, you can't change it. It will be added to {{personName}}'s assessment history.",
      confirm: "Complete assessment",
    },
    // A guest sending the assessment they were asked for to whoever asked.
    sendDialog: {
      title: "Send to {{asker}}?",
      body: "Once sent, you can't change it or open it again, and this link will stop working.",
      copyAfterSending: "You can download a copy after sending.",
      confirm: "Send to {{asker}}",
      // Sending with items left unanswered warns first, counting them.
      titleNotAnswered: "Send this assessment to {{asker}}?",
      notAnswered_one: "{{count}} of {{of}} items hasn't been answered",
      notAnswered_other: "{{count}} of {{of}} items haven't been answered",
      notAnsweredInDomain: "{{domain}}: {{count}}",
      sentAsNotAnswered:
        "They'll be sent as Not answered. Once sent, you can't change it or open it again. You can download a copy after sending.",
      goBack: "Go back and answer them",
      confirmNotAnswered: "Send with {{count}} not answered",
      refused:
        "This could not be sent. It may have been sent already, or your access may have ended; open the link in your email again to check.",
    },
    assessedOn: {
      invalid: "Enter the day the answers describe, as a real date.",
    },
    layout: {
      back: "Back to profile",
      record: "Assessment record",
      reference: "Assessment ID",
    },
    overview: {
      domains: "Domains",
      complete: "{{completed}} of {{included}} complete",
      domain: "Domain",
      status: "Status",
      itemsActioned: "Items actioned",
      itemsOf: "{{done}} of {{total}}",
      needComment_one: "{{count}} needs a comment",
      needComment_other: "{{count}} need a comment",
      open: "Open",
      openDomain: "Open {{domain}}",
      domainsComplete: "{{completed}} of {{included}} domains complete",
      itemsActionedOf: "{{done}} of {{total}} items actioned",
      domainsCompleteShort: "{{completed}} of {{included}} domains",
      itemsActionedOfShort: "{{done}} of {{total}} items",
      ready: {
        approval: "All domains complete · ready to submit",
        completion: "All domains complete · ready to complete",
      },
      domainItemsOf: "{{done}} of {{total}} items",
      domainItemsOfShort: "{{done}}/{{total}}",
      expandAll: "Expand all",
      collapseAll: "Collapse all",
      showItems: "Show items in {{domain}}",
      hideItems: "Hide items in {{domain}}",
      requiresComments: "Requires comments ({{count}})",
      action: {
        start: "Start",
        continue: "Continue",
        review: "Review",
      },
      actionDomain: {
        start: "Start domain",
        continue: "Continue domain",
        review: "Review domain",
      },
      actionLabel: {
        start: "Start {{domain}}",
        continue: "Continue {{domain}}",
        review: "Review {{domain}}",
      },
      notRated: "Not rated",
      commentRequired: "Comment required",
      commentAdded: "Comment added",
      details: "Record details",
      state: {
        not_opened: "Not opened yet",
        not_started: "Not started",
        in_progress: "In progress",
        completed: "Completed",
      },
      noDomains: "This instrument has no domains to complete.",
      thisAssessment: "This assessment",
      assessor: "Assessor",
      assessorUnknown: "Not shown",
      approver: "Approver",
      noApprover: "None yet",
      started: "Started",
      lastActivity: "Last activity",
      instrumentVersion: "Instrument version",
      submitForApproval: "Submit for approval",
      submit: "Complete assessment",
      domainsLeft: {
        approval:
          "Every domain has to be complete before you can submit. {{left}} left.",
        completion: "Every domain has to be complete first. {{left}} left.",
      },
      // Said in place of `domainsLeft` while a reassessment that carried
      // ratings over has domains its assessor has not yet opened.
      domainsToOpen: {
        approval:
          "Open every domain before submitting. {{left}} still to open.",
        completion:
          "Open every domain before completing. {{left}} still to open.",
      },
      // Said in place of `domainsLeft` where something outside every domain
      // holds readiness back, leaving no domain to count.
      notReady: {
        approval: "Every domain has to be complete before you can submit.",
        completion: "Every domain has to be complete first.",
      },
      delete: "Delete assessment",
      carryOverRefused:
        "Last time's ratings could not be carried over, so this assessment starts empty.",
      notOpen: "This assessment is no longer open for changes.",
      toPersonPage: "Go to the profile",
    },
    review: {
      domains_one: "{{count}} domain",
      domains_other: "All {{count}} domains",
      summary_one: "Submitted by {{assessor}}, {{date}}. {{count}} item.",
      summary_other: "Submitted by {{assessor}}, {{date}}. {{count}} items.",
      domain_one: "{{domain}} · {{count}} item",
      domain_other: "{{domain}} · {{count}} items",
      expandAll: "Expand all",
      collapseAll: "Collapse all",
      item: "Item",
      rating: "Rating",
      comment: "Comment",
      noItems: "Nothing in this domain was answered.",
      formUnavailable:
        "The names of the domains, items and ratings couldn't be loaded, so the answers are shown as they were stored.",
      retry: "Try again",
      submitted: "Submitted",
      approved: "Approved",
      approverUnknown: "Not shown",
      notSubmitted: "This assessment has no submission to show.",
    },
    answer: {
      yes: "Yes",
      no: "No",
    },
    history: {
      title: "Full history",
      back: "Back to the {{date}} assessment",
      backToAssessment: "Back to the assessment",
      backToPersonPage: "Back to the young person",
      instrument: "Instrument",
      instrumentOption: "{{family}} · {{count}} approved · latest {{date}}",
      instrumentHint: "Opens on the most recently assessed instrument",
      inRange_one: "{{count}} approved assessment, {{from}}",
      inRange_other: "{{count}} approved assessments, {{from}} to {{to}}",
      groupItems_one: "{{count}} item",
      groupItems_other: "{{count}} items",
      from: "From",
      to: "to",
      previousYear: "Previous year",
      all: "All",
      itemsLabel: "Items",
      items: {
        current: "Current action items",
        historic: "Historic action items",
        all: "All items",
      },
      chart: {
        title: "Progression",
        label:
          "How many items each column of the summary showed at each approved assessment",
        latest: "Latest in range",
        latestNeeds: "needs for focus",
        latestStrengths: "strengths to build",
        latestHistory: "underlying",
        allShown_one: "The assessment in range shown.",
        allShown_other: "All {{count}} assessments in range shown.",
        windowed:
          "{{count}} assessments in range, {{size}} at a time. Drag the slider or use Earlier and Later.",
        explained:
          "Each point sits above its assessment. Dashed lines mark a move or a new assessor.",
        date: "Assessment",
      },
      event: {
        assessor: "new assessor",
      },
      window: {
        label: "Assessments the chart and table show",
        earlier: "‹ Earlier",
        later: "Later ›",
        showing: "Showing {{from}} to {{to}}",
      },
      table: {
        label: "Each item's rating at each approved assessment",
        item: "Item",
        across: "Across the range",
      },
      group: {
        history: "Underlying items",
        needs: "Needs · {{domain}}",
        strengths: "Strengths",
        caregiver: "Caregiver needs",
      },
      across: {
        not_rated: "Not rated now",
        same: "Same throughout",
        changed: "Changed",
        persistent: "Persistent",
        new: "New",
        recurring: "Recurring",
        resolved: "Resolved",
        still_to_build: "Still to build",
        built: "Built",
      },
      rated: "Rated {{rating}}",
      notRated: "Not rated",
      loading: "Loading the history...",
      none: "There is no approved assessment to show.",
      noneInRange: "No approved assessment falls in this range.",
      noneLaidOut_one:
        "The {{count}} assessment in this range couldn't be read or laid out, so there is nothing to show.",
      noneLaidOut_other:
        "None of the {{count}} assessments in this range could be read or laid out, so there is nothing to show.",
      notShown_one:
        "{{count}} assessment in this range couldn't be read or laid out, so it isn't shown.",
      notShown_other:
        "{{count}} assessments in this range couldn't be read or laid out, so they aren't shown.",
      unreadable: "This young person's history couldn't be read.",
      nothingListed: {
        current: "Nothing calls for action in the latest assessment.",
        historic: "Nothing called for action in this range.",
        all: "Nothing was rated.",
      },
    },
    full: {
      expandAll: "Expand all domains",
      collapseAll: "Collapse all domains",
      fullText: "Show full text",
      filterLabel: "Items to show",
      filter: {
        all: "All items",
        action: "Action items",
        comment: "Items with a comment",
        not_answered: "Not answered",
      },
      counts: {
        answered: "{{count}} answered",
        notAnswered: "{{count}} not answered",
        items_one: "{{count}} item",
        items_other: "{{count}} items",
        actions_one: "{{count}} action item",
        actions_other: "{{count}} action items",
        comments_one: "{{count}} with a comment",
        comments_other: "{{count}} with a comment",
      },
      domainItems_one: "{{count}} item",
      domainItems_other: "{{count}} items",
      domainActions_one: "{{count}} action item",
      domainActions_other: "{{count}} action items",
      domainNotAnswered: "{{count}} not answered",
      domainNoneAnswered: "None answered",
      notAnswered: "Not answered",
      notAnsweredMark: "-",
      sentWithoutRating: "Sent without a rating.",
      expand: "Expand",
      collapse: "Collapse",
      aboutDomain: "About this domain",
      aboutItem: "About this item",
      showLess: "Show less",
      answer: "Answer",
      given: "given",
      showRatings: "Show all ratings",
      hideRatings: "Hide the ratings",
      ratingsOffered: "Every rating {{title}} offers",
      comment: "Assessor's comment",
      noComment: "No comment",
      readMore: "Read more",
      readLess: "Show less",
      itemHistory: "Item history",
      empty: {
        all: "Nothing was answered.",
        action: "No item calls for action.",
        comment: "No item has a comment.",
        not_answered: "Every item was answered.",
      },
      loading: "Loading the assessment...",
      formUnavailable:
        "The assessment couldn't be shown because the instrument couldn't be loaded.",
      retry: "Try again",
      notSubmitted: "This assessment has nothing submitted to show yet.",
    },
    summary: {
      tabs: {
        label: "Views of this assessment",
        summary: "Summary",
        full: "Full assessment",
        history: "Full history",
        downloadPdf: "Download PDF",
        downloadPdfFailed: "The PDF couldn't be downloaded. Try again.",
      },
      column: {
        history: "Underlying items",
        needs: "Needs for focus",
        strengths: "Strengths to build",
        caregiver: "Caregiver needs",
      },
      neverActioned: "never an action item",
      topRated: "{{count}} rated {{rating}}",
      topAnsweredYes: "{{count}} answered yes",
      domains_one: "{{count}} domain",
      domains_other: "{{count}} domains",
      count_one: "{{count}} item",
      count_other: "{{count}} items",
      countFrom: "{{from}} to",
      countMoved: "{{from}} before, {{to}} now",
      tally: {
        resolved: "{{count}} resolved",
        new: "{{count}} new",
        escalated: "{{count}} escalated",
        eased: "{{count}} eased",
        continuing: "{{count}} continuing",
        built: "{{count}} built",
        changed: "{{count}} changed",
      },
      section: {
        rating: "Rated {{rating}}",
        departed: {
          history: "No longer rated",
          needs: "No longer for focus",
          strengths: "No longer to build",
          caregiver: "No longer for focus",
        },
      },
      change: {
        new: "New",
        escalated: "Escalated",
        eased: "Eased",
        weakened: "Weakened",
        continuing: "Continuing",
        resolved: "Resolved",
        built: "Built",
        same: "Same",
        changed: "Changed",
        not_rated: "Not rated",
        not_answered: "Not answered",
      },
      // Read out in place of the badges, which a screen reader does not hear;
      // `answer` is for an item answered yes or no.
      rating: {
        now: "Rated {{rating}}",
        moved: "Rated {{previous}} before, {{rating}} now",
        notRatedBefore: "Not rated before, {{rating}} now",
        notRatedNow: "Rated {{previous}} before, not rated now",
        notAnsweredBefore: "Not answered before, {{rating}} now",
        notAnsweredNow: "Rated {{previous}} before, not answered now",
      },
      answer: {
        now: "{{rating}}",
        moved: "{{previous}} before, {{rating}} now",
        notRatedBefore: "Not answered before, {{rating}} now",
        notRatedNow: "{{previous}} before, not answered now",
        notAnsweredBefore: "Not answered before, {{rating}} now",
        notAnsweredNow: "{{previous}} before, not answered now",
      },
      empty: {
        history: "Nothing in the history is rated above 0.",
        needs: "No need is rated 2 or 3.",
        strengths: "No strength is rated 2 or 3.",
        caregiver: "No caregiver need is rated 2 or 3.",
      },
      footer: {
        background: "Needs in background",
        assets: "Assets to use",
        caregiverResources: "Caregiver resources",
        ratedOne: "rated 1",
        ratedZeroOrOne: "rated 0 or 1",
        withRating: "{{title}} · {{rating}}",
        none: "None.",
        notAnswered: "Not answered",
        notAnsweredInDomain: "{{domain}} · {{count}}",
      },
      groupBy: "Group by",
      grouping: {
        rating: "Rating",
        domain: "Domain",
      },
      shown: "{{rated}} items rated · {{shown}} shown here",
      shownWithNotAnswered:
        "{{rated}} items rated · {{notAnswered}} not answered · {{shown}} shown here",
      ratingsOnly: "Ratings only. Comments are on Full assessment.",
      footnote: {
        single:
          "Needs rated 0 aren't shown. Every item, with its comment, is on Full assessment.",
        comparison:
          "Ratings only. Comments are on Full assessment for each assessment.",
      },
      facts: {
        at: {
          approval: "At approval, {{dates}}",
          completion: "At completion, {{dates}}",
          submission: "Submitted {{dates}}",
        },
        dates: "{{first}} and {{second}}",
        placement: "Placement",
        assessor: "Assessor",
        approver: "Approver",
        instrument: "Instrument",
        both: "(both)",
        notKnown: "Not known",
        noneRecorded: "None recorded",
        movedTo: ", then",
        guest: {
          nonDefinitive: "Non-definitive assessment",
          guest: "Guest",
          sent: "Sent {{date}}",
          answered: "{{answered}} of {{of}} answered",
          askedBy: "Asked by",
        },
      },
      compare: {
        label: "Compare with",
        none: "No comparison",
        justBefore: "{{date}} · the one before",
        listHeading: "{{family}} assessments, newest first",
        listHeadingAny: "Assessments, newest first",
        definitive: "Definitive",
        contribution: "contribution",
        contributionBy: "contribution by",
        chosenContribution: "{{date}} · contribution",
        needsHeading: "Needs for focus",
        needsFor: "needs for focus",
        tag: {
          justBefore: "The one before",
          yearAgo: "A year ago",
        },
        clear: "Clear",
        candidates_zero: "No earlier assessment to compare with",
        candidates_one: "{{count}} earlier assessment",
        candidates_other: "{{count}} earlier assessments",
        describesJustBefore: "The assessment just before this one",
        describesEarlier: "An earlier approved assessment",
        describesAny: "An earlier assessment",
        describesContribution: "A contribution by {{name}}",
        describesContributionAnyone: "A colleague's contribution",
        problem: {
          unreadable:
            "The assessment to compare with can't be read, so this one is shown alone.",
          other_person:
            "That assessment is about someone else, so this one is shown alone.",
          other_instrument:
            "That assessment is on a different instrument, so this one is shown alone.",
          same_assessment:
            "An assessment can't be compared with itself, so it is shown alone.",
          not_finished:
            "Only an approved or completed assessment can be compared with, so this one is shown alone.",
          not_earlier:
            "Only an earlier assessment can be compared with, so this one is shown alone.",
          not_submitted:
            "That assessment has nothing submitted to compare with, so this one is shown alone.",
        },
      },
      loading: "Loading the summary...",
      formUnavailable:
        "The summary couldn't be built because the instrument couldn't be loaded.",
      retry: "Try again",
      uncategorised:
        "This version of the instrument doesn't say what kind of domain each group is, so its answers can't be summarised. Full assessment shows every answer.",
      withheld: "You can see this assessment exists but not what it says.",
      notSubmitted: "This assessment has nothing submitted to summarise yet.",
    },
    item: {
      backToOverview: "Back to overview",
      position: "{{position}} / {{count}}",
      progress: "Item {{position}} of {{count}} in this domain",
      back: "Back",
      next: "Next",
      returnToOverview: "Return to overview",
      comment: {
        optionalLabel: "Comment (optional)",
        requiredLabel: "Comment, required for a rating of {{value}}",
        readOnlyLabel: "Comment",
        optionalPlaceholder: "Add anything that explains this rating",
        requiredPlaceholder: "Explain why you have given this rating",
        missing: "You can carry on but this item won't be complete.",
        saving: "Saving...",
        saved: "Saved",
        failed:
          "Your comment hasn't been saved. Check your connection and try again.",
      },
      rating: "Rating",
      notRated: "Not rated",
      carriedRating: "Rating from last assessment",
      previousComment: {
        caption: "Comment from last assessment, {{date}}",
        add: "Add to your comment",
        copy: "Copy",
        copied: "Copied",
        failed: "Couldn't copy. Select the text instead.",
      },
      answer: "Answer",
      notAnswered: "Not answered",
      noComment: "No comment",
      save: {
        saving: "Saving...",
        saved: "Saved",
        failed:
          "Your rating hasn't been saved. Check your connection and try again.",
      },
      tryAgain: "Try again",
      domainChange: {
        added: "The {{domain}} domain has been added.",
        removed: "The {{domain}} domain has been removed.",
        answersDropped_one: "The answer in it won't be kept.",
        answersDropped_other: "The {{count}} answers in it won't be kept.",
      },
      unknownDomain: "This domain is not part of this assessment.",
      unknownItem: "This item is not part of this domain.",
      emptyDomain: "This domain asks nothing at the moment.",
    },
    page: {
      assessedOn: "Assessed on",
      assessedOnHelp:
        "The day these answers describe the $t(person.singular) as of.",
      assessedOnAfterToday: "The day assessed cannot be after today.",
      save: {
        saved: "All changes saved.",
        saving: "Saving",
        unsaved: "Unsaved changes.",
        failed:
          "Could not save your latest changes. They will be saved with your next answer.",
      },
      submit: {
        refused:
          "This could not be submitted. It may already have been submitted elsewhere; go back to the profile to check.",
        failed: "Could not submit. Check your connection and try again.",
      },
      contentsWithheld: "You can see this exists but not its contents.",
      failed: {
        not_found: "This assessment could not be found.",
        not_open: "This assessment is no longer open to fill in.",
        unavailable: "This assessment's form could not be opened. Try again.",
      },
    },
    form: {
      optionalGroup: "{{title}} (optional)",
      commentOn: "Comment on {{title}}",
      submit: "Submit",
      document: "What would be sent",
      evidence: "Evidence of what was shown",
      passes: "The form would accept this.",
      refuses: "The form would refuse this:",
      notDecorated: "These decorators cannot be applied to this instrument:",
      // Keyed by the problem codes the form collector reports, so the words
      // are this language's and the code says only what is wrong.
      problems: {
        "must-be-answered": "Answer this question.",
        "must-have-a-comment": "Add a comment explaining this answer.",
        "answered-but-not-asked":
          "This question is not being asked, so it cannot have an answer.",
        "comment-without-its-answer":
          "Answer this question before commenting on it.",
        "not-on-the-form":
          "This answer is for a question the form does not ask.",
        "not-valid-for-instrument":
          "This is not an answer the assessment accepts.",
        "not-a-calendar-date": "Enter a real date.",
        "after-today": "This date cannot be after today ({{today}}).",
        "not-checkable":
          "This answer cannot be checked. Try entering it again.",
        "cannot-check": "This form cannot be checked.",
      },
    },
  },

  // A person's own account.
  //
  // The keys under `name` and `email` are rendered by `UserProfileForm`, which
  // staff also use on somebody else's account — so those must not say "your".
  // The page-level strings above them may, because each page has one subject.
  profile: {
    title: "Your account",
    description:
      "Your name appears beside everything you do in $t(app.name). You can change it here.",
    save: "Save",
    upToDate: "Nothing to save.",
    openYours: "Your account",
    name: {
      label: "Full name",
      help: "Shown beside everything done from this account.",
      // Keys emitted by `UserNameSchema` in @thalamos/haven-common, which sets
      // translation keys rather than sentences so the wording lives here.
      required: "Enter a name.",
      tooLong: "A name can be at most 255 characters.",
    },
    email: {
      label: "Email address",
      help: "Comes from the identity this account signs in with. Nobody can change it in $t(app.name), including its administrators.",
    },
  },

  // The panels that filter a listing by one of its columns.
  listingFilters: {
    search: "Search",
    option: "{{label}} ({{count}})",
    include: "Include {{label}} ({{count}})",
    from: "From",
    to: "To",
    rangeBackwards: "The To date is before the From date.",
    clear: "Clear",
    clearAll: "Clear all",
    remove: "Remove {{filter}} filter",
    chip: "{{column}}: {{value}}",
    rangeBoth: "{{from}} to {{to}}",
    rangeFrom: "from {{from}}",
    rangeTo: "up to {{to}}",
  },

  // The records of the people being assessed.
  personListing: {
    title: "Young people",
    description:
      "The $t(person.plural) you are involved with, or can see through your organisation.",
    filter: "Filter by name or UPN",
    name: "Name",
    upn: "UPN",
    dateOfBirth: "Date of birth",
    nhsNumber: "NHS number",
    notRecorded: "Not recorded",
    dutyOfCare: "Duty of care",
    placement: "Placement",
    yourInvolvement: "Your involvement",
    caseManager: "Case manager",
    lastApproved: "Last approved assessment",
    notKnown: "Not known",
    notAssigned: "Not assigned",
    none: "None",
    viewerThrough: "Viewer through {{organisation}}",
    unassigned: "Unassigned. Still needs: {{missing}}",
    onlyUnassigned: "Only Unassigned profiles ({{count}})",
    noResults: "No $t(person.plural) match.",
    loadFailed: "Could not load this list. Reload the page to try again.",
    notPermitted:
      "What you are working as cannot see $t(person.plural). Choose something else to work as, or ask whoever administers $t(app.name).",
    backToList: "All $t(person.plural)",
    searchOrAdd: "Search or add a $t(person.singular)",
    empty: {
      title: "You do not have any current involvements",
      body: "Search to find a $t(person.singular), or to create a record if they are not already known.",
    },
  },

  personMatch: {
    title: "Search $t(person.singular)",
    searchBy: "Search by",
    byUpn: "UPN",
    byDemographics: "Demographics",
    upn: "Unique Pupil Number (UPN)",
    given: "First name",
    family: {
      label: "Last name (required)",
      required: "Enter their last name.",
    },
    similarSoundingGiven: "Match similar sounding first names",
    similarSoundingFamily: "Match similar sounding last names",
    dateOfBirthKnown: {
      label: "Do you know their date of birth? (required)",
      required: "Say whether you know their date of birth.",
    },
    dateOfBirth: {
      label: "Date of birth",
      required: "Enter their date of birth.",
    },
    yes: "Yes",
    no: "No",
    searchFirst:
      "Search first, so the same $t(person.singular) is not recorded twice. You can create a new record once you have searched.",
    search: "Search",
    continue: "Continue",
    clear: "Clear search",
    create: "Create $t(person.singular)",
    results: "Results",
    found: {
      title: "Possible matches found",
      body: "These $t(person.plural) may match the details you entered. Select the right one to open their record. If none of them is right, search again or create a new record.",
    },
    none: {
      title: "No matches found",
      body: "No $t(person.singular) you can see matches these details. Check the spelling and date of birth and search again, or create a new record.",
    },
    tooMany: {
      title: "Too many results to show",
      body: "More than {{limit}} $t(person.plural) match these details. Add a first name or a date of birth, or turn off similar sounding names, to narrow the search.",
    },
    failed: "The search could not be run. Try again.",
    notPermitted:
      "What you are working as cannot search for $t(person.plural) to register. Choose something else to work as, or ask whoever administers $t(app.name).",
  },

  personPage: {
    newTitle: "Register a $t(person.singular)",
    newDescription:
      "Record what you know. Anything can be corrected later, and every version of the record is kept.",
    record: "Record",
    notFound: "That record does not exist, or you cannot see it.",
    loadFailed: "Could not load this record. Reload the page to try again.",
    mergedNotice:
      "The record you followed was merged into this one on {{date}}. This is the surviving record.",
    save: "Save",
    register: "Register",
    upToDate: "Nothing to save.",
    saveFailed:
      "Could not save that. Check the details and try again — if it keeps failing, contact support.",

    // The heading a record is read under.
    banner: {
      fullName: "Full Name",
      preferredName: "Preferred Name",
      age: "Age",
      dateOfBirth: "Date of Birth",
      legalStatus: "Legal / DoL Status",
      careSetting: "Care Setting",
      designatedAssessor: "Designated Assessor",
      allDetails: "All details",
      noCurrentName: "No current name",
    },

    dateOfBirth: {
      label: "Date of birth",
      help: "Leave blank if not yet known.",
      future: "Their date of birth cannot be in the future.",
    },

    // What has been done to the record. The event names are deliberately plain
    // statements of the act: the log is read when something has gone wrong, and
    // a reader is matching what they see against what they were told happened.
    activity: {
      open: "Activity log",
      title: "What has been done to this record",
      description:
        "Every change to this record, to who can reach this $t(person.singular) and to their assessments, most recent first.",
      empty: "Nothing has been recorded against this record yet.",

      close: "Close",

      // Whether reach was given or taken away. The act alone is the same words a
      // dozen times over, so the involvement is what a reader is looking for.
      added: "Added",
      ended: "Ended",
      endsOn: "Ends on {{date}}",
      endedOn: "Ended {{date}}",
      // An involvement held one at a time passing to its next holder, which
      // the trail records as the old one ending as superseded.
      handedOver: "Handed over",
      involvementHandedOver: "Involvement handed over",
      // A change said with its cause, composed once so every rendering of it —
      // shown or read out — says it the same way.
      changeWithCause: "{{change}} · {{cause}}",

      event: {
        person_created: "Record opened",
        person_updated: "Record corrected",
        person_enrolled: "Case set up",
        person_merged:
          "Declared the same $t(person.singular) as another record",
        person_unmerged: "Merge undone",
        person_archived: "Record archived",
        person_unarchived: "Record unarchived",
        admin_person_read: "Record read by a $t(app.name) administrator",
        involvement_started: "Involvement added",
        involvement_ended: "Involvement ended",
        // Said where an entry carries nothing more specific; otherwise the
        // assessment's own log wording is used.
        assessment_started: "Assessment started",
        assessment_submitted: "Assessment submitted",
        assessment_approved: "Assessment approved",
        assessment_returned: "Assessment returned",
        assessment_reopened: "Assessment reopened",
        assessment_deleted: "Assessment deleted",
        assessment_undeleted: "Assessment put back",
        assessment_pdf_downloaded: "Assessment downloaded as a PDF",
        guest_assessments_shared: "Assessments shared with a guest",
        shared_assessment_read: "Shared assessment opened by a guest",
      },

      // The party is named where the involvement the entry points at is still
      // to hand.
      involvement: "{{kind}}",
      involvementWithParty: "{{kind}} — {{party}}",

      // Which parts of the record a correction changed. The trail names them as
      // the record's own fields, so they are given words here.
      corrected: "$t(personPage.activity.event.person_updated) — {{fields}}",
      field: {
        names: "names",
        dateOfBirth: "date of birth",
      },
    },

    // Several keys below are what the person schemas in @thalamos/haven-common
    // emit as messages — translation keys rather than sentences — so a refusal's
    // wording lives here; a key a schema emits with no entry here renders raw.
    names: {
      title: "Names",
      description:
        "A $t(person.singular) may be known by more than one name. Record each, and when it applied.",
      add: "Add another name",
      remove: "Remove this name",
      use: "Type of name",
      given: "Given names",
      givenHelp: "Separate several with spaces.",
      family: "Family name",
      periodStart: "Used from",
      periodEnd: "Used until",
      required: "Add at least one name.",
      forenameAndSurname: "Enter their first name and last name.",
      useLabel: {
        official: "Official",
        usual: "Usual",
        nickname: "Nickname",
        old: "Previous",
        maiden: "Maiden",
        temp: "Temporary",
        anonymous: "Anonymous",
      },
    },
    name: {
      partEmpty: "Enter a name, or remove the empty part.",
      partTooLong: "A name can be at most 255 characters.",
      required: "Enter a family name or a given name.",
      periodReversed: "The name cannot stop being used before it started.",
    },
    date: {
      invalid: "Enter a date.",
    },

    assessments: {
      title: "Current assessments",
      column: {
        kind: "Kind",
        instrument: "Assessment",
        assessor: "Assessor",
        approver: "Approver",
        state: "State",
        started: "Started",
        actions: "Actions",
      },
      definitive: "Definitive",
      nonDefinitive: "Non-definitive",
      guest: "Guest",
      askedBy: "Guest, asked by {{asker}}",
      guestUntil: "Until {{date}}",
      unknownAssessor: "Not known",
      noApprover: "None yet",
      continue: "Continue",
      continueOf: "Continue {{assessor}}'s assessment, started {{date}}",
      view: "View",
      viewOf: "View {{assessor}}'s assessment, started {{date}}",
      review: "Review",
      reviewOf: "Review {{assessor}}'s assessment, started {{date}}",
      delete: "Delete",
      deleteOf: "Delete {{assessor}}'s assessment, started {{date}}",
      why: {
        assessor_only: "Only its assessor can open it",
        approver_only: "Only its approver can review it",
      },
      empty: {
        active: "No assessments underway",
        unassigned: "No assessment can start yet. It still needs: {{missing}}.",
        archived: "No assessment can start. The record is archived.",
      },
      goToCaseInvolvement: "Go to Case involvement",
      loadFailed:
        "Could not load the assessments. Reload the page to try again.",
      status: {
        open: "In progress",
        submitted: "Submitted for approval",
        returned: "Returned",
        approved: "Approved",
        completed: "Completed",
      },
      // What has been done to one assessment, in plain statements of the act.
      activity: {
        open: "Activity log",
        title: "What has been done to this assessment",
        version: "Instrument version: {{instrument}}",
        empty: "Nothing has been recorded against this assessment yet.",
        event: {
          assessment_started: "Started",
          assessment_draft_started: "Began filling in the form",
          assessment_submitted: "Submitted",
          assessment_deleted: "Deleted",
          assessment_undeleted: "Put back",
          assessment_pdf_downloaded: "Downloaded as a PDF",
          guest_assessments_shared: "Shared with a guest",
          shared_assessment_read: "Opened by a guest",
          assessment_approved: "Approved",
          assessment_returned: "Returned",
          assessment_reopened: "Reopened",
        } satisfies Record<AssessmentActivityKind, string>,
        startedAgainst: "Started against {{instrument}}",
        startedDefinitive:
          "Started the definitive assessment against {{instrument}}",
        startedContributing:
          "Started a contributing assessment against {{instrument}}",
        submittedAttempt: "Attempt {{number}} submitted",
        submittedForApproval: "Attempt {{number}} submitted for approval",
        submittedCompleting:
          "Attempt {{number}} submitted, completing the assessment",
        approvedAttempt: "Attempt {{number}} approved",
        returnedAttempt: "Attempt {{number}} returned",
        reopenedFrom: "Reopened from attempt {{number}}",
        draftFrom: "Began again from attempt {{number}}",
        againstVersion: "{{act}}, against {{instrument}}",
      },
      deleteDialog: {
        title: "Delete this assessment?",
        started: "This assessment was started by {{assessor}} on {{date}}.",
        progress: "{{completed}} of {{included}} domains completed.",
        removedFrom:
          "This assessment will be removed from {{personName}}'s record and won't appear in their assessment history or in reporting. Its ratings won't carry into any future assessment.",
        removedFromUnnamed:
          "This assessment will be removed from the young person's record and won't appear in their assessment history or in reporting. Its ratings won't carry into any future assessment.",
        cannotBeUndone:
          "If you proceed, the assessment will be permanently deleted. This action cannot be undone.",
        reason: "Why are you deleting this assessment?",
        confirm: "Delete assessment",
        failed:
          "Could not delete the assessment. Reload the page and try again.",
      },
      history: {
        title: "Assessment history",
        column: {
          kind: "Kind",
          instrument: "Assessment",
          assessor: "Assessor",
          approvedBy: "Approved by",
          finished: "Finished",
        },
        unknownApprover: "Not known",
        noApprovalNeeded: "No approval needed",
        finished: {
          approved: "Approved {{date}}",
          completed: "Completed {{date}}",
        },
        view: "View",
        viewOf: {
          approved: "View {{assessor}}'s assessment, approved {{date}}",
          completed: "View {{assessor}}'s assessment, completed {{date}}",
        },
        summary: "Summary",
        summaryOf: {
          approved: "Summary of {{assessor}}'s assessment, approved {{date}}",
          completed: "Summary of {{assessor}}'s assessment, completed {{date}}",
        },
        why: {
          contents_withheld: "You can see this exists but not its contents",
        },
        empty: "No assessments have been completed yet",
        loadFailed:
          "Could not load the assessment history. Reload the page to try again.",
        showing: "Showing {{shown}} of {{total}}.",
        // Said in place of `showing` where no larger page can be asked for, so
        // a reader is told which part of the history they hold rather than
        // offered a control that would fetch nothing.
        showingMostRecent: "Showing the most recent {{shown}} of {{total}}.",
        showOlder: "Show older",
        showOlderFailed: "Could not load older assessments. Try again.",
        sharedWith_one: "Shared with {{count}} guest",
        sharedWith_other: "Shared with {{count}} guests",
      },
      start: "Start assessment",
      startDialog: {
        title: "Start an assessment",
        body: "You are starting an assessment for {{name}}.",
        definitive: "This is a definitive assessment.",
        approver:
          "When submitted, this assessment will go to {{approver}} for approval.",
        noApprover:
          "No Approver is in place yet. You can start and work on this assessment, but you can't submit it until an Approver is added.",
        version: "Instrument version",
        instrument: "Instrument",
        instrumentHint:
          "Set to the instrument from the previous assessment for {{name}}. You can choose another.",
        carryOver: "Carry over ratings from the previous assessment",
        fromYours: "From your assessment {{date}}.",
        fromTheirs: "From {{assessor}}'s assessment {{date}}.",
        approvedBy: "Approved by {{approver}}.",
        earlierVersion:
          "The previous assessment was done on {{version}}, an earlier version. Items in both versions carry over. Anything new in this version starts unrated.",
        noCarryOver:
          "Previous ratings won't carry over. The previous assessment for {{name}}, {{finished}} {{date}}, used {{instrument}}. Ratings only carry over on the same instrument, so this assessment will start empty.",
        noCarryOverBack:
          "Previous ratings won't carry over. The previous assessment for {{name}}, {{finished}} {{date}}, used {{instrument}}, a later version. Ratings only carry over onto the same version or a later one, so this assessment will start empty.",
        finished: {
          approved: "approved",
          completed: "completed",
        },
        none: "No assessment is available to this organisation yet.",
        start: "Start assessment",
        failed: "Could not start the assessment. Try again.",
        versionsFailed:
          "Could not read which assessments this organisation may use. Close this and try again.",
      },
      definitiveUnderWay: "A definitive assessment is already open",
    },
    identifiers: {
      title: "Identifiers",
      description:
        "What other systems call this $t(person.singular). These are recorded when the record is created and travel with it through a merge; they are not typed here.",
      none: "No identifiers are recorded.",
      lookUp: "Look up in PDS",
      notAvailable: "Not available yet",
    },
    tabs: {
      label: "Profile",
      assessments: "Assessments",
      identity: "Identity",
      caseInvolvement: "Case involvement",
    },
    state: {
      active: "Active",
      unassigned: "Unassigned",
      archived: "Archived",
    },
    unassigned: {
      banner: "This profile is unassigned. It still needs: {{missing}}.",
      name: "Name who runs the case",
    },
    archive: {
      open: "Archive",
      banner: "This profile is archived.",
      unarchive: "Unarchive",
      title: "Archive this profile?",
      description:
        "No assessment can be started from an archived profile until it is unarchived. Assessments already under way carry on, and nothing else about the record changes.",
      confirm: "Archive",
      failed: "Could not archive this profile. Reload the page and try again.",
      unarchiveFailed:
        "Could not unarchive this profile. Reload the page and try again.",
    },

    homeAddress: {
      title: "Home address",
      address: "Address",
      postcode: "Postcode",
      searchHint:
        "Start typing to search for the address, or type it in full if it is not found.",
      notRecorded: "Not recorded",
      addressRequired: "Enter their address.",
      addressTooLong: "An address can be at most 255 characters.",
      postcodeRequired: "Enter their postcode.",
      postcodeTooLong: "A postcode can be at most 8 characters.",
    },

    creation: {
      given: {
        label: "First name (required)",
        required: "Enter their first name.",
      },
      family: {
        label: "Last name (required)",
        required: "Enter their last name.",
      },
      dateOfBirthKnown: {
        label: "Do you know their date of birth? (required)",
        required: "Say whether you know their date of birth.",
      },
      dateOfBirth: {
        required: "Enter their date of birth.",
      },
      homeAddressKnown: {
        label: "Do you know their home address? (required)",
        required: "Say whether you know their home address.",
      },
      upn: "Unique Pupil Number (UPN)",
      yes: "Yes",
      no: "No",
      submit: "Create $t(person.singular)",
      backToSearch: "Back to search",
    },

    identifier: {
      systemInvalid: "Enter the system as a URI.",
      systemTooLong: "A system URI can be at most 255 characters.",
      valueRequired: "Enter the identifier.",
      valueTooLong: "An identifier can be at most 255 characters.",
      nhsNumberInvalid: "That is not a valid NHS number.",
      upnLength: "A UPN is 13 characters.",
      upnCharacters:
        "A UPN is a letter, then 11 digits, then a digit or a letter. I, O and S are not used.",
      upnCheckLetter: "That is not a valid UPN. Check each character.",
      duplicate: "This identifier is already listed.",
    },

    merges: {
      title: "Merged records",
      description:
        "Records found to be the same $t(person.singular). Nothing on a merged record is changed; it is shown here as it was registered, and the merge can be undone.",
      none: "No other records have been merged into this one.",
      mergedOn: "Merged on {{date}}",
      reasonGiven: "Reason given",
      createdBy: "Registered by another organisation",
      undo: "Undo this merge",
      undoTitle: "Undo the merge",
      undoDescription:
        "The record will stand on its own again, exactly as it was. Say why.",
      undoConfirm: "Undo merge",
      undoFailed: "Could not undo the merge. Reload the page and try again.",
      start: "Merge another record into this one",
      dialogTitle: "Merge a record into this one",
      dialogDescription:
        "Find the duplicate. It will be treated as the same $t(person.singular) as this record: nothing on either is changed, and the merge can be undone later.",
      search: "Find the duplicate by name or UPN",
      noMatches: "No other records match.",
      selected: "Merging {{name}} into this record.",
      reason: "Why are these records the same $t(person.singular)?",
      reasonHelp:
        "Kept with this record, so anyone who can see the record can see why.",
      confirm: "Merge",
      cancel: "Cancel",
      failed:
        "Could not merge these records. Check that both still stand on their own and try again.",
      administratorsOnly:
        "Only a records administrator can merge records or undo a merge.",
    },
    merge: {
      reasonRequired: "Say why these records are the same $t(person.singular).",
      reasonTooLong: "A reason can be at most 1000 characters.",
    },
  },

  // Thalamos' own area. Not part of the product's vocabulary — these strings are
  // read by staff, so they name the machinery ("accounts", "organisations")
  // rather than using the softer words the assessment screens use.
  admin: {
    title: "Administration",
    notAnAdministrator:
      "This area is for $t(app.name) administrators. If you administer $t(app.name), choose your administrator role to see it.",
    noResults: "Nothing matches.",
    loadFailed: "Could not load this list. Reload the page to try again.",
    pageRange: "{{from}}–{{to}} of {{total}}",
    sortBy: "Sort by {{column}}",
    filterBy: "Filter by {{column}}",
    filteredBy: "Filter by {{column}} (filtered)",

    groups: {
      directory: "Directory",
      oversight: "Oversight",
    },

    search: {
      label: "Find an administration page",
      placeholder: "Search administration…",
      noMatches: "No administration page matches.",
    },

    users: {
      title: "Users",
      filter: "Filter by name or email",
      name: "Name",
      email: "Email",
      account: "Account",
      backToList: "All users",
      notFound: "That account does not exist, or you cannot see it.",
      loadFailed: "Could not load this account. Reload the page to try again.",
      notAnOrganisationAdmin:
        "This area is for an organisation's admins. If you are one, choose your admin role to see it.",
      create: {
        open: "Create user",
        title: "Create a user",
        name: "Full name",
        email: {
          label: "Email address",
          help: "The address they will sign in with. It can't be changed afterwards.",
          // Keys emitted by `NewAccountEmailSchema` in @thalamos/haven-common.
          invalid: "Enter an email address, like name@example.org.",
          tooLong: "An email address can be at most 320 characters.",
        },
        organisation: "Organisation",
        role: "Role",
        submit: "Create user",
        exists: "This email address already has an account.",
        addToOrganisation: "Add to organisation",
        failed:
          "Could not create that account. Try again — if it keeps failing, contact support.",
      },
      passwordReset: {
        open: "Send password reset email",
        title: "Send a password reset email",
        description: "{{name}} will be emailed a link to choose a password.",
        confirm: "Send email",
        sent: "{{name}} has been emailed a link to choose a password.",
        signsInThroughOrganisation:
          "This account signs in through its organisation, so has no password to reset.",
        failed:
          "Could not send the email. Try again — if it keeps failing, contact support.",
      },
    },

    organisations: {
      title: "Organisations",
      filter: "Filter by name",
      name: "Name",
      type: "Type",
      allTypes: "All types",
      add: {
        open: "Add organisation",
        title: "Add an organisation",
        name: "Name",
        type: "Type",
        configuration: "Configuration",
        configurationHint:
          "The configuration it is set to. You can change it later on its Configuration tab.",
        noConfigurations:
          "There is no configuration to set it to yet. Create one under Configuration chains first.",
        configurationsLoading: "Loading configurations…",
        configurationsFailed:
          "Could not load the configurations. Reload the page to try again.",
        submit: "Add organisation",
        failed:
          "Could not add that organisation. Try again — if it keeps failing, contact support.",
      },
    },

    organisation: {
      title: "Organisation",
      backToList: "All organisations",
      notFound: "That organisation does not exist.",
      loadFailed:
        "Could not load this organisation. Reload the page to try again.",
      name: "Name",
      type: "Type",
      update: "Update",
      updateFailed:
        "Could not save every change. The page shows what was saved — try again, and if it keeps failing, contact support.",
      tabs: {
        label: "Organisation sections",
        details: "Basic details",
        relationships: "Relationships",
        users: "Users",
        configuration: "Configuration",
        structure: "Structure",
        counted: "{{label}} ({{count}})",
      },
      readsThroughMembers: {
        label: "Reads approved assessments through its members",
        description:
          "Whether this organisation reads what an approved or completed assessment says about a $t(person.singular) it reaches only through one of its members. Each change is recorded against you.",
        failed:
          "Could not change this setting. Reload the page and try again — if it keeps failing, contact support.",
        confirmOnTitle:
          "Let {{name}} read approved assessments through its members?",
        confirmOffTitle:
          "Stop {{name}} reading approved assessments through its members?",
        confirmOn:
          "{{name}}'s users will be able to open approved and completed assessments of $t(person.plural) it reaches only through its members. It applies straight away.",
        confirmOff:
          "{{name}}'s users will no longer be able to open approved and completed assessments of $t(person.plural) it reaches only through its members. They still see that each assessment exists and where it stands. It applies straight away.",
        alsoRenamed: "It is also renamed to {{name}}.",
        alsoRetyped: "Its type also changes to {{type}}.",
        confirm: "Save",
      },
      configuration: {
        label: "Configuration",
        none: "Not set to a configuration",
        changeOnTab: "Changed on the Configuration tab.",
      },
      configurationTab: {
        intro:
          "The configuration decides what {{name}} may do, such as which assessments it can start and the words its screens use. Only Thalamos admins can change it.",
        current: "Set to",
        usedBy_one: "Used by {{count}} organisation",
        usedBy_other: "Used by {{count}} organisations",
        whereItSits: "Where it sits",
        usedHere: "Used here",
        change: "Change configuration",
        changeTo: "Configuration for {{name}}",
        review: "Review change",
        reviewHint: "You confirm the change before anything is saved.",
        confirmTitle: "Set {{name}} to {{to}}?",
        confirmFrom: "From {{from}}. It applies straight away to {{name}}.",
        confirmOnlyThis:
          "Only {{name}} changes. Its members keep their own configurations.",
        confirm: "Change configuration",
        changeFailed: "Could not change its configuration. Try again.",
        settings: "What applies to {{name}}",
        settingsCount_one: "{{count}} setting",
        settingsCount_other: "{{count}} settings",
        scopesFailed:
          "Could not load the configuration chains. Reload the page to try again.",
      },
      relationships: {
        description:
          "How this organisation is linked to others. Each link shows on both organisations.",
        relation: "Relationship",
        organisation: "Organisation",
        remove: "Remove",
        add: "Add a relationship",
        addSubject: "{{name}} is",
        addAction: "Add",
        changeFailed:
          "Could not make that change. Reload the page and try again — if it keeps failing, contact support.",
        none: "This organisation is not linked to any other.",
        loadFailed:
          "Could not load this organisation's relationships. Reload the page to try again.",
        relations: {
          member_of: "Member of",
          has_member: "Has member",
          operated_by: "Operated by",
          operates: "Operates",
        },
        confirm: {
          addTitle: "Add this relationship?",
          removeTitle: "Remove this relationship?",
          joins:
            "{{member}} becomes a Member of {{group}}. It applies straight away. Its configuration does not change.",
          leaves:
            "{{member}} stops being a Member of {{group}}. It applies straight away. Its configuration does not change.",
          gains:
            "Changes who can see and admin $t(person.plural), and who manages people's accounts. {{group}} and every organisation above it gain View of the $t(person.plural) {{member}}, or an organisation below it, holds the duty of care or placement for, and Admin where it holds the duty of care. Those that read approved assessments through their members can also read what those assessments say. Their admins can manage the accounts of people at {{member}} and the organisations below it, and grant or remove their capacities.",
          loses:
            "Changes who can see and admin $t(person.plural), and who manages people's accounts. Through {{member}}, {{group}} and every organisation above it stop reaching the $t(person.plural) {{member}}, or an organisation below it, holds the duty of care or placement for: View, Admin where it holds the duty of care, and what approved assessments say for those that read them through their members. Their admins stop managing the accounts of people at {{member}} and the organisations below it. Any reach they have another way stays.",
          gainsAcknowledgement:
            "I understand {{group}} and the organisations above it gain access to {{member}}'s $t(person.plural), and their admins to its people's accounts",
          losesAcknowledgement:
            "I understand {{group}} and the organisations above it lose access to {{member}}'s $t(person.plural), and their admins to its people's accounts",
          operatesAdded:
            "{{operator}} will be recorded as operating {{operated}}. It applies straight away.",
          operatesRemoved:
            "{{operator}} will no longer be recorded as operating {{operated}}. It applies straight away.",
          operatesAccess:
            "No change to who can see or admin $t(person.plural): nothing travels through Operates.",
          add: "Add relationship",
          remove: "Remove relationship",
        },
      },
      structure: {
        description:
          "Where {{name}} sits, and which permissions travel up each membership.",
        loadFailed:
          "Could not load this organisation's structure. Reload the page to try again.",
        truncated:
          "This structure is too large to show in full, so the organisations furthest from this one are left out.",
        diagram: "Structure of {{name}}",
        cardLabel: "{{name}}, {{type}}, {{standing}}",
        cardLabelWithSetting: "{{name}}, {{type}}, {{standing}}, {{setting}}",
        zoomIn: "Zoom in",
        zoomOut: "Zoom out",
        fit: "Fit",
        picker: "Show what travels up",
        carried: {
          view: "View",
          admin: "Admin",
          "approved-assessments": "Approved assessments",
        },
        rule: "{{permission}} travels up a membership when the member holds {{involvements}}.",
        ruleGated:
          "{{permission}} travels up a membership when the member holds {{involvements}}, and only into an organisation with “{{setting}}” on. Only the receiving organisation's setting counts, not those of the organisations in between.",
        reaches: {
          view: "Within an organisation, View reaches everyone who holds a capacity there.",
          admin: "Within an organisation, Admin reaches only its admins.",
          "approved-assessments":
            "Within an organisation, approved assessments reach everyone who holds a capacity there.",
        },
        levels: {
          label: "Levels shown",
          one: "One level",
          all: "All levels",
        },
        standing: {
          focus: "This organisation",
          above_one: "Member of, {{count}} level up",
          above_other: "Member of, {{count}} levels up",
          below_one: "Has member, {{count}} level down",
          below_other: "Has member, {{count}} levels down",
          operates: "Operated by this organisation",
          "operated-by": "Operates this organisation",
        },
        setting: "{{setting}}: {{state}}",
        on: "on",
        off: "off",
        more: "+{{count}} more",
        moreLabel: "Show {{count}} more",
        edgeCarried: "From {{involvements}}",
        edgeNotCarried: "“{{setting}}” off",
        operates: "Operates: reporting only, carries nothing",
        memberships: {
          heading: "Memberships shown",
          none: "No memberships are shown.",
          carried_one:
            "Into {{receiver}} from {{count}} direct member: carries {{permission}} up when the member holds {{involvements}}.",
          carried_other:
            "Into {{receiver}} from {{count}} direct members: carries {{permission}} up when the member holds {{involvements}}.",
          notCarried_one:
            "Into {{receiver}} from {{count}} direct member: does not carry {{permission}} up, because {{receiver}} has “{{setting}}” off.",
          notCarried_other:
            "Into {{receiver}} from {{count}} direct members: does not carry {{permission}} up, because {{receiver}} has “{{setting}}” off.",
          operates:
            "{{operator}} operates {{operated}}: reporting only, carries nothing.",
        },
        key: {
          heading: "Key",
          carriedTerm: "Blue arrow",
          carried:
            "Member of, carries the selected permission up. The label says which involvements it comes from.",
          notCarriedTerm: "Grey dashed arrow",
          notCarried: "Member of, does not carry it.",
          operatesTerm: "Grey dashed line",
          operates: "Operates. Reporting only, never carries anything.",
        },
        configuration: {
          heading: "Configuration in use",
          permission: "Permission",
          from: "Carried up from",
          gatedBy: "Gated by",
          ungated: "Nothing",
        },
      },
      users: {
        description:
          "Who may do what at this organisation. Granting one is a decision that is recorded against you: it says the person has been trained and is entitled to see the assessments held by this organisation.",
        user: "User",
        alreadyHeld: "This account already holds that capacity here.",
        none: "Nobody holds a capacity at this organisation.",
        loadFailed:
          "Could not load who works at this organisation. Reload the page to try again.",
      },
    },

    memberships: {
      title: "Capacities",
      description:
        "What this account may do, and where. Granting one is a decision that is recorded against you: it says the person has been trained and is entitled to see the assessments held by that organisation.",
      organisation: "Organisation",
      role: "Role",
      none: "This account holds no capacities, so it can do nothing in $t(app.name) yet. Grant one below.",
      loadFailed:
        "Could not load this account's capacities. Reload the page to try again.",
      changeFailed:
        "Could not make that change. Reload the page and try again — if it keeps failing, contact support.",
      grant: "Grant a capacity",
      grantAction: "Grant",
      revokeAction: "Remove",
      alreadyHeld: "This account already holds that capacity.",
      reconciled: "Set by directory group",
    },

    // Admin is granted like a membership but is a different decision from case
    // work.
    adminMemberships: {
      title: "Admin",
      description:
        "The organisations where this account is an admin. An admin manages the records of young people the organisation is accountable for, and decides who is involved with them.",
      none: "This account is not an admin at any organisation.",
      grant: "Make admin",
      alreadyHeld: "This account is already an admin at that organisation.",
    },

    configuration: {
      title: "Configuration",
      scopesTitle: "Configuration chains",
      organisationsTitle: "Organisation configuration",

      // The chain is what makes a value set in one place reach another, so the
      // page says so rather than leaving an administrator to infer it from a
      // tree of names.
      scopesDescription:
        "Each configuration takes its settings from the one it sits under, and changes only what is different. Organisations are set to a configuration. A configuration that sits under nothing starts a chain of its own, and a chain can be a single configuration. A configuration decides what an organisation may do; it gives nobody access to a $t(person.singular)'s record.",
      organisationsDescription:
        "The configuration each organisation is set to. Changes are held, across pages and filters, until you save them.",
      organisationsDescriptionCounted_one:
        "The configuration the {{count}} organisation is set to. Changes are held, across pages and filters, until you save them.",
      organisationsDescriptionCounted_other:
        "The configuration each of the {{count}} organisations is set to. Changes are held, across pages and filters, until you save them.",

      scope: {
        name: {
          label: "Name",
          placeholder: "For example, RCC preset 4",
          required: "Give this configuration a name.",
          tooLong: "That name is too long.",
        },
        parent: {
          label: "Sits under",
          none: "Nothing - this is the top of a chain",
          top: "{{name}} (level 1)",
          below: "{{name}} (level {{level}}, {{chain}} chain)",
        },
        showMore_one: "Show {{count}} more configuration in this chain",
        showMore_other: "Show {{count}} more configurations in this chain",
        add: "New configuration",
        newTitle: "New configuration",
        newHint:
          "It takes every setting from the configuration it sits under until you change one. Sitting under nothing starts a new chain.",
        create: "Create configuration",
        save: "Save",
        cancel: "Cancel",
        rename: "Rename or move",
        renameTitle: "Rename or move {{name}}",
        chainTitle: "{{name}} chain",
        configurations_one: "{{count}} configuration",
        configurations_other: "{{count}} configurations",
        levels_one: "{{count}} level",
        levels_other: "{{count}} levels",
        organisations_one: "{{count}} organisation",
        organisations_other: "{{count}} organisations",
        configuration: "Configuration",
        level: "Level",
        levelOf: "Level {{level}}",
        usedBy: "Used by",
        notUsedDirectly: "Not used directly",
        placement: "Level {{level}} · {{chain}} chain",
        usedByLink_one: "used by {{count}} organisation",
        usedByLink_other: "used by {{count}} organisations",
        empty:
          "No chains yet. Create a configuration under nothing to start one.",
        notFound:
          "This configuration does not exist. It may have been removed.",
        backToChains: "Back to configuration chains",
        loadFailed:
          "Could not load the configuration chains. Reload the page to try again.",
        saveFailed: "Could not save this configuration. Try again.",
        // Which organisations saving applies to, counting those set to the
        // configurations under this one.
        appliesToNone:
          "No organisation is set to {{name}} or to a configuration under it.",
        appliesToHere_one:
          "It applies straight away to the {{count}} organisation set to {{name}}.",
        appliesToHere_other:
          "It applies straight away to the {{count}} organisations set to {{name}}.",
        appliesToBelow_one:
          "It applies straight away to {{count}} organisation: {{here}} set to {{name}}, and {{below}} set to configurations under it.",
        appliesToBelow_other:
          "It applies straight away to {{count}} organisations: {{here}} set to {{name}}, and {{below}} set to configurations under it.",
        appliesToBelowNamed_one:
          "It applies straight away to {{count}} organisation: {{here}} set to {{name}}, and {{below}} set to {{belowName}}, which sits under it.",
        appliesToBelowNamed_other:
          "It applies straight away to {{count}} organisations: {{here}} set to {{name}}, and {{below}} set to {{belowName}}, which sits under it.",
        moveHint:
          "A new name saves straight away. Moving it changes what it takes from the configuration above, so a move is confirmed first.",
        moveTitle: "Move {{name}}?",
        moveFrom: "It moves from under {{from}} to under {{to}}.",
        moveLevel: "{{name}} becomes level {{level}}.",
        moveLevelWith_one:
          "{{name}} becomes level {{level}}, and {{below}} moves with it.",
        moveLevelWith_other:
          "{{name}} becomes level {{level}}, and {{below}} move with it.",
        renamedTo: "It is renamed from {{from}} to {{to}}.",
        moveConfirm: "Move and save",
      },

      organisation: {
        name: "Organisation",
        memberOf: "Member of",
        memberOfAny: "Any organisation",
        configuration: "Configuration",
        none: "Not set",
        filter: "Search by name",
        allConfigurations: "All configurations",
        type: "Type",
        allTypes: "All types",
        setTo: "Configuration for {{name}}",
        settings: "Own settings",
        settingsFor: "Own settings for {{name}}",
        settingsTitle: "{{name}}'s own settings",
        settingsHint:
          "What this organisation states for itself, over what its configuration gives it.",
        close: "Close",
        nothingChanged: "There is nothing to save.",
        namedTwice: "An organisation was changed twice.",
        tooMany:
          "Too many organisations to change at once. Save some of them, then change the rest.",
        unsavedWas: "Unsaved. Was {{name}}",
        unsavedOnly: "Unsaved changes only ({{count}})",
        unsavedCount_one: "{{count}} unsaved change.",
        unsavedCount_other: "{{count}} unsaved changes.",
        unsavedWhere:
          "{{here}} on this page, {{elsewhere}} elsewhere. Nothing changes until you save.",
        discardAll: "Discard all",
        confirmTitle_one: "Save {{count}} change?",
        confirmTitle_other: "Save {{count}} changes?",
        confirmIntro_one:
          "{{count}} organisation moves to a different configuration. It applies straight away.",
        confirmIntro_other:
          "{{count}} organisations move to a different configuration. It applies straight away.",
        move_one: "{{from}} → {{to}} · {{count}} organisation",
        move_other: "{{from}} → {{to}} · {{count}} organisations",
        confirm_one: "Save {{count}} change",
        confirm_other: "Save {{count}} changes",
        saveFailed: "Those changes were refused: {{reason}}",
        saveFailedUnknown: "Could not save those changes. Try again.",
      },

      settings: {
        title: "Settings",
        setting: "Setting",
        applies: "What applies",
        source: "Set at",
        override: "Set here",
        overrideFor: "Set {{setting}} here",
        sourceDefault: "Haven default",
        chain: "Takes its settings from {{chain}}",
        on: "On",
        off: "Off",
        none: "None",
        // Says both halves: what applies, and what this configuration asked for —
        // because one that states something wider than it inherits is not
        // broken, it is being held to what the one above allows.
        clamped:
          "This configuration asked for {{asked}}, and is held to what it inherits.",
        // A different reason from a clamp, and a different thing to do about it:
        // one means the configuration above allows less, this means another
        // setting here made this one impossible.
        reconciled:
          "This configuration asked for {{asked}}, which another setting here rules out.",
        inherited: "Inherits {{value}}",
        groups: {
          starting_an_assessment: "Starting an assessment",
          reassessment: "Reassessment",
          records: "Records",
          language_and_wording: "Language and wording",
        },
        keys: {
          recordMergeEnabled: "Merging records",
          readsApprovedAssessmentsThroughMembers:
            "Reads approved assessments through its members",
          availableInstrumentVersions: "Assessments available to choose",
          reassessmentCarryOverDays: "Carrying ratings over",
          availableLanguages: "Languages offered",
          defaultLanguage: "Default language",
          vocabularyPack: "Vocabulary",
        },
        descriptions: {
          recordMergeEnabled:
            "Admins can merge two records for the same $t(person.singular).",
          readsApprovedAssessmentsThroughMembers:
            "Its users can open approved assessments of $t(person.plural) it reaches through its members.",
          availableInstrumentVersions:
            "What people can pick from when they start one. Can only remove options from what it inherits.",
          reassessmentCarryOverDays:
            "How long after an assessment is finished a reassessment can take its ratings.",
          availableLanguages:
            "Languages people can choose. Can only remove languages from what it inherits.",
          defaultLanguage:
            "The language a new user starts in. One of the languages offered.",
          vocabularyPack: 'Whether screens say "child" or "young person".',
        },
        units: {
          days: "days",
        },
        choices: {
          instrumentVersions: {
            sampleCore10: "Sample core 1.0",
            sampleCore11: "Sample core 1.1",
            sampleCore12: "Sample core 1.2",
            cansComprehensive10: "CANS Comprehensive 1.0",
            cansMini10: "CANS Mini 1.0",
          },
          languages: {
            en: "English",
            cy: "Welsh",
          },
          vocabularyPacks: {
            youngPerson: "Young person",
            child: "Child",
          },
        },
        saveFailed: "That change was refused: {{reason}}",
        nothingChanged: "There is nothing to save.",
        namedTwice: "A setting was changed twice.",
        unsavedWas: "Unsaved. Was {{value}}",
        unsavedWasInherited: "Unsaved. Was inherited: {{value}}",
        unsaved: {
          count_one: "{{count}} unsaved change.",
          count_other: "{{count}} unsaved changes.",
          discard: "Discard",
          save: "Save changes",
          confirmTitle_one: "Save {{count}} change to {{name}}?",
          confirmTitle_other: "Save {{count}} changes to {{name}}?",
          changes: "What changes",
          // Asserted on every configuration confirmation, without a tick. A
          // setting changed on the configuration pages that changed access
          // would make it untrue.
          noAccessChange:
            "No change to who can see or admin $t(person.plural).",
          alsoChanges: "Also changes {{settings}}.",
          alsoChangesNothing: "No other setting changes.",
          alsoChangesReading: "Working out which other settings change…",
          alsoChangesFailed: "Could not work out which other settings change.",
          confirm_one: "Save {{count}} change",
          confirm_other: "Save {{count}} changes",
          cancel: "Cancel",
          appliesToOwn: "It applies straight away to {{name}} only.",
        },
        loadFailed:
          "Could not load these settings. Reload the page to try again.",
      },
    },

    persons: {
      title: "Young people",
      reference: "Profile ID or profile link",
      open: "Open",
      identityWithheld:
        "This $t(person.singular)'s identity is withheld. Reading the profile, and every change you make to it, is recorded against it.",
      notAReference:
        "Enter a profile ID, or a link to the profile, as the customer gave it.",
      notFound: "No profile has that ID.",
      failed: "Could not read that profile. Try again.",
      personId: "Profile",
      state: "State",
      openedBy: "Opened by",
      // Stands in for the child's name wherever a dialog would say it.
      thisPerson: "this $t(person.singular)",
    },

    trail: {
      title: "Audit trail",
      from: "From date",
      to: "To date",
      kinds: "Entry types",
      allKinds: "All entry types",
      userId: "Acted by (user id)",
      resourceId: "About (resource id)",
      entry: "",
      showEntry: "Show the whole entry",
      close: "Close",
      when: "When",
      kind: "Action",
      who: "Who",
      sessionContext: "Acting as",
      resources: "About",
    },
  },

  session: {
    noMemberships:
      "Your account cannot act anywhere yet. Someone who administers $t(app.name) needs to give you a role at an organisation before you can work on assessments. You can still check the name on your account.",
  },

  errors: {
    title: "Something went wrong",
    body: "Try reloading the page. If the problem continues, contact support.",
    reload: "Reload",
    sessionContextFailed:
      "Could not act in that capacity. Ask whoever administers $t(app.name) to check your access, then try again.",
    // An outage is not a refusal, so nothing here sends the user to an
    // administrator who will find nothing wrong with their access.
    sessionContextUnavailable:
      "Could not change capacity just now. Check your connection, then try again.",
    userLoadFailed:
      "Could not load your details. Check that the API is running and reachable, then reload.",
    profileSaveFailed:
      "Could not save that. Check the name and try again — if it keeps failing, contact support.",
    notFound: "That page does not exist.",
  },

  // The roles a person can hold. Named for the reader rather than by the
  // stored value, which is why these are not simply title-cased in the
  // component.
  roles: {
    assessor: "Assessor",
    // Named for the work rather than plainly: "Administrator" beside the
    // $t(app.name) one below reads as the same thing, and is not.
    organisation_admin: "Records administrator",
    responsible_user: "Case manager",
    approver: "Approver",
    viewer: "Viewer",
    thalamos_admin: "$t(app.name) administrator",
  },

  // The kinds of work a session can act in at an organisation.
  sessionContextKinds: {
    "case-work": "Case work",
    organisation_administration: "Unit administration",
    thalamos_administration: "$t(app.name) administration",
  },

  // Who is involved with a $t(person.singular), and what each involvement
  // conveys. The vocabulary is deliberately the words a case manager would use
  // rather than the slugs the rows carry.
  access: {
    title: "Who can reach this record",
    description:
      "Everybody who can see this $t(person.singular), and what their involvement lets them do.",
    organisations: "Organisations",
    users: "People",
    // Ended involvements are kept and convey nothing, so they are folded away
    // rather than left competing with the ones that do.
    ended_one: "{{count}} involvement has ended",
    ended_other: "{{count}} involvements have ended",
    since: "since {{date}}",
    at: "at {{organisation}}",
    givenBy: "Given by {{name}}",
    empty: "Nobody has been recorded as involved with this record yet.",
    unassigned: "$t(access.kind.responsible_user): Unassigned",
    loadFailed: "Could not load who can reach this record.",
    reachTitle: "What you can reach",
    reachDescription:
      "Every $t(person.singular) you can see, and the involvement that gives you each.",
    reachEmpty: "You are not recorded as involved with anybody.",

    caseInvolvement: {
      title: "Case involvement",
      column: {
        role: "Role",
        heldBy: "Held by",
        onBehalfOf: "On behalf of",
        since: "Since",
        until: "Until",
        actions: "Actions",
      },
      one: "One",
      anyNumber: "Any number",
      setAtCreation: "Set at creation",
      holder: {
        localAuthority: "a local authority",
        careSetting: "a care setting",
        notTheDesignatedAssessor: "not the designated assessor",
      },
      notAssigned: "Not assigned",
      none: "None",
      neededToStart: "Needed to start an assessment",
      cannotChange: "Can't be changed",
      guest: "No organisation (guest)",
      sinceDate: "Since {{date}}",
      untilDate: "Until {{date}}",
      expired: "Expired",
      add: {
        organisation: "Add organisation",
        user: "Add person",
      },
      addLabel: {
        organisation: "Add organisation as {{role}}",
        user: "Add person as {{role}}",
      },
      designateFirst: "Add a designated assessor first",
      replace: "Replace",
      replaceLabel: "Replace {{name}} as {{role}}",
      end: "End",
      endLabel: "End {{name}} as {{role}}",
      endRefused: {
        kept_by_the_record: "Can't be changed",
        replaced_instead: "Can't be ended. Replace them instead",
        assessment_open:
          "Can't be ended while an assessment is open. Replace them instead",
        awaiting_approval:
          "Can't be ended while an assessment is waiting for approval",
      },
    },

    column: {
      who: "Who",
      involvement: "Involvement",
      since: "Since",
      grantedBy: "Given by",
      conveys: "Conveys",
      person: "$t(person.singular)",
    },

    // The kinds of involvement, in the words the service uses for them.
    kind: {
      created_by: "Opened the record",
      duty_of_care: "Duty of care",
      placement: "Placement",
      responsible_user: "Runs the case",
      designated_assessor: "Designated assessor",
      assessor: "Contributing assessor",
      viewer: "Oversight",
      approver: "Approver",
      shared_assessment_reader: "Reads shared assessments",
    },

    // What each action lets somebody do, as a person would say it.
    action: {
      read: "read",
      read_identity_withheld: "read, identity withheld",
      read_assessments: "read assessments",
      read_log: "audit",
      update: "correct",
      merge: "merge",
      assess: "assess",
      approve: "approve",
      assign: "assign",
      allocate_responsible_user: "name the case manager",
      involve_guest: "involve a guest",
    },

    // The same actions in a sentence, because one word does not say what a
    // membership lets somebody do to a child's record.
    actionDescription: {
      read: "See this $t(person.singular)'s record, and which assessments there are about them and where each stands.",
      read_identity_withheld:
        "See how this $t(person.singular)'s record is handled — its state, who opened it and who is involved — with their identity withheld.",
      read_assessments:
        "Read what each finished assessment about this $t(person.singular) says: the answers and comments.",
      read_log:
        "See what has been done to this $t(person.singular)'s record: who opened it, who corrected it, and who was given or lost access to them.",
      update:
        "Change what the record says about the $t(person.singular) themselves — the names they are known by, and their date of birth.",
      merge:
        "Declare two records to be the same $t(person.singular), or undo that.",
      assess:
        "Make assessments about this $t(person.singular). An assessment is one person's: there is no partial or joint completion.",
      approve:
        "Approve a definitive assessment. Only a definitive assessment needs approving.",
      assign:
        "Decide who else is involved: add an involvement, or end one. Held by the person running the case.",
      allocate_responsible_user:
        "Name the person who will run the case. Spent when the record is opened.",
      involve_guest:
        "Ask somebody outside the service to complete an assessment about this $t(person.singular), or share finished assessments with them.",
    },

    // Why an involvement ended, in the words the service uses for each cause.
    cause: {
      superseded: "handed over",
      "left-role": "left their role or organisation",
      "organisation-withdrew": "organisation no longer involved",
      "placement-moved": "child moved placement",
      "no-longer-commissioned": "no longer commissioned",
      "organisation-closed": "organisation closed",
      "no-longer-needed": "no longer needed",
      "added-in-error": "added in error",
      other: "other",
      withdrawn: "withdrawn",
      "case-closed": "case closed",
      declined: "declined by the guest",
      sent: "sent by the guest",
      revoked: "access ended",
      expired: "access expired",
    },

    guest: "no organisation",
    conveysNothing: "nothing",

    refresh: "Refresh",

    allocatingResponsibleUser: {
      title: "Name who runs the case",
      description:
        "They hold this $t(person.singular)'s case until they hand it on, and decide who else is involved.",
      submit: "Name them",
      failed: "Could not name them. Try again.",
      changed:
        "Who runs this case has changed since you opened it. Refresh to see the latest.",
    },
    replacingResponsibleUser: {
      title: "Replace Case Manager",
      warning:
        "{{name}} will no longer manage this $t(person.singular)'s involvements, unless another part of theirs allows it.",
      alreadyRunsIt:
        "{{name}} already runs this case for that body. Choose somebody else, or another of their bodies.",
      reason: "Why is the Case Manager changing?",
      submit: "Replace Case Manager",
      failed: "Could not hand the case on. Try again.",
      changed:
        "Who runs this case has changed since you opened it. Refresh to see the latest.",
    },

    // Ending one involvement.
    ending: {
      title: "End an involvement",
      description:
        "An involvement that has ended conveys nothing, and stays on the record so a withdrawal reads as a decision somebody made.",
      cause: "Why",
      causePlaceholder: "Select reason",
      // The causes as choices to pick from, worded for the kind of party
      // whose involvement is ending; `cause` words them mid-sentence for both.
      causes: {
        user: {
          superseded: "Handed over to someone else",
          "left-role": "Left their role or organisation",
          "organisation-withdrew": "Organisation no longer involved",
          "no-longer-needed": "No longer needed in this role",
          "added-in-error": "Added in error",
          other: "Other",
        },
        organisation: {
          "placement-moved": "Child moved placement",
          superseded: "Handed over to another organisation",
          "no-longer-commissioned": "No longer commissioned",
          "organisation-closed": "Organisation closed",
          "no-longer-needed": "No longer needed",
          "added-in-error": "Added in error",
          other: "Other",
        },
      },
      reason: "Additional information",
      reasonHint: "Optional. Shown in this record's history.",
      reasonHintRequired:
        "Needed when the reason is Other. Shown in this record's history.",
      reasonInvalid:
        "Give additional information of no more than 4,000 characters.",
      timing: "When",
      endNow: "End now",
      endOnDay: "End on a date",
      endsOn: "Ends on",
      endsOnPast: "Choose today or a later day.",
      endsOnAlready: "It already ends on that day. Choose another day.",
      consequenceNow:
        "{{name}}'s access as {{role}} ends now. The involvement stays on the record.",
      consequence:
        "{{name}}'s access as {{role}} ends on {{date}}. The involvement stays on the record.",
      becomesUnassigned:
        "This profile will become Unassigned. No new assessment can start until {{role}} is added.",
      submit: "End involvement",
      failed: "The involvement could not be ended.",
      changed:
        "This involvement has changed since you opened it. Refresh to see the latest.",
    },

    // Finding somebody, and what a picker says about who it found.
    picker: {
      search: "Search by name",
      searchHint:
        "Type at least {{count}} characters. This search finds people you can already name; it does not list them.",
      searching: "Searching",
      noResults: "Nobody found by that name.",
      noOrganisations: "No bodies of that kind are recorded.",
      filter: "Filter by name",
      guest: "As a guest, with no body behind them",
      ineligible: "Cannot take this part at {{organisation}}",
      clear: "Choose somebody else",
      distant: "Not part of this case",
      distantExplanation:
        "{{organisation}} has no live involvement with this $t(person.singular), and is not the body you are working in. You can still involve them — this is a prompt to think, not a refusal.",
      distantGuest:
        "They have no body behind them, so nothing connects them to this $t(person.singular)'s care but this involvement. You can still involve them — this is a prompt to think, not a refusal.",
    },

    addInvolvements: {
      title: {
        user: {
          designated_assessor: "Add Designated Assessor",
          approver: "Add Approver",
          assessor: "Add Assessors",
          viewer: "Add people with View",
        },
        organisation: {
          duty_of_care: "Add Duty of Care",
          placement: "Add Placement",
          viewer: "Add organisations with View",
        },
      },
      info: {
        user: {
          designated_assessor:
            "Add a person as Designated Assessor for {{name}}. Only people who can assess are shown.",
          approver:
            "Add a person as Approver for {{name}}. Only people who can approve are shown.",
          assessor:
            "Add people as Assessors for {{name}}. Only people who can assess are shown.",
          viewer:
            "Add people with View for {{name}}. They can see the profile but take no action.",
        },
        organisation: {
          duty_of_care:
            "Add the organisation with Duty of Care for {{name}}. Only local authorities are shown.",
          placement:
            "Add where {{name}} is placed. Only care settings are shown.",
          viewer:
            "Add organisations with View for {{name}}. They can see the profile but take no action.",
        },
      },
      alreadyHolds: {
        duty_of_care: "Already holds the Duty of Care for {{name}}",
        placement: "Already the Placement for {{name}}",
        designated_assessor: "Already the Designated Assessor for {{name}}",
        approver: "Already the Approver for {{name}}",
        assessor: "Already an Assessor for {{name}}",
        viewer: "Already has View for {{name}}",
      },
      excluded: {
        designated_assessor:
          "The Approver for {{name}}, so can't also be the Designated Assessor",
        approver:
          "The Designated Assessor for {{name}}, so can't also be the Approver",
      },
      listed: "Already on the list",
      searchPeople: "Search by name",
      searchOrganisations: "Search by organisation name",
      memberships_one: "{{count}} organisation membership",
      memberships_other: "{{count}} organisation memberships",
      onBehalfOf: "Involved on behalf of",
      onBehalfOfPlaceholder: "Choose one of their organisations",
      addUser: "Add person",
      addOrganisation: "Add organisation",
      oneOnly: "Only one can be added to this role.",
      remove: "Remove {{name}}",
      chosen: "{{name}} ({{organisation}})",
      consequence:
        "Everyone at {{organisation}} will be able to see {{name}}'s profile.",
      confirmOrganisations: "Add organisations",
      failed:
        "Nothing was added. Try again, or check who already holds a role.",
      changed:
        "Who is involved has changed since you opened it. Refresh to see the latest.",
    },
  },

  // The kinds of body Haven recognises, in the words the service uses.
  organisation: {
    type: {
      local_authority: "Local authority",
      provider: "Provider",
      residential_unit: "Residential unit",
      education: "Education",
      health: "Health",
      oversight: "Oversight body",
      // Every organisation recorded before kinds existed. It is nameable to
      // nothing, so saying so is more use than leaving the field blank.
      unknown: "Kind not recorded",
    },
    // Keys emitted by `CreateOrganisationRequestSchema` and
    // `RenameOrganisationRequestSchema` in @thalamos/haven-common.
    name: {
      required: "Enter a name.",
      tooLong: "A name can be at most 255 characters.",
    },
  },

  // What every log says the same way, whatever it is a log of.
  activity: {
    loadFailed:
      "Could not load the activity log. Close this and try again — if it keeps failing, contact support.",
    showing: "Showing {{shown}} of {{total}}.",
    // Said in place of `showing` where no further page can be asked for, so a
    // reader is told which part of the log they hold rather than offered a
    // control that would fetch nothing.
    showingMostRecent: "Showing the most recent {{shown}} of {{total}}.",
    showMore: "Show older",

    // {{time}} is a time of day, not a full timestamp.
    attribution: "{{who}} · {{sessionContext}} · {{time}}",
    // Why, in the words of whoever acted, where the record holds a reason.
    reason: "Reason: {{reason}}",
    // The same, where a cause from a closed set already says why in brief.
    additionalInformation: "Additional information: {{reason}}",
    // Read out in place of the line above, which is three values separated by
    // dots and says nothing about which is which.
    attributionLabel: "By {{who}}, acting as {{sessionContext}}, at {{time}}",
    sessionContextLabel: "{{organisation}} ({{role}})",

    // An entry with no author must not render as a blank.
    system: "$t(app.name)",
    noSessionContext: "No capacity chosen",
  },

  common: {
    loading: "Loading",
    cancel: "Cancel",
    close: "Close",
  },

  toasts: {
    // eMHA's words. Alt+T moves focus to the toasts.
    region: "Notifications Alt+T",
  },

  // Asking somebody outside the service to complete an assessment.
  askGuest: {
    open: "Ask someone outside the service",
    title: "Ask someone outside the service",
    intro:
      "They'll be asked to complete the whole assessment of {{personName}}'s needs. We'll email them a link.",
    name: "Their name",
    email: "Their work email address",
    emailInvalid: "Enter their email address, like name@school.org.uk.",
    relation: "Who they are to the young person",
    relationRequired: "Choose who they are to the young person.",
    relationDescription: "Describe who they are",
    jobTitle: "Their job title",
    message: "Message (optional)",
    messageHelp: "We'll include this in the email.",
    version: "Instrument version",
    versionRequired: "Choose a version.",
    versionsFailed:
      "The instrument versions could not be read. Close this and try again.",
    noVersions: "There is no instrument version to ask for.",
    endsOn: "How long they have",
    endsAfterAMonth: "1 month, until {{date}}",
    endsOnChosenDay: "Until a date I choose",
    chosenDay: "Access ends after",
    chosenDayHelp: "They have until the end of this day to send it.",
    invalid: "Check this answer.",
    send: "Send request",
    back: "Back",
    earlierGuest: {
      question: "{{email}} has been asked before. Is this the same person?",
      withJobTitle: "{{name}}, {{jobTitle}}",
      somebodyNew: "No, {{name}} is someone else",
    },
    outcome: {
      already_asked:
        "This person already has an assessment to complete for this young person.",
      no_date_of_birth:
        "{{personName}}'s record has no date of birth, which a guest needs to sign in. Add it on the Identity tab first.",
      failed: "The request could not be sent. Try again.",
    },
    sent: "Request sent to {{guest}}. They have until {{date}} to send it.",
  },

  // Ending a guest's access, for either kind of guest, wherever it is offered.
  endGuestAccess: {
    open: "End access",
    openOf: "End {{name}}'s access",
    title: "End {{name}}'s access?",
    loses: {
      complete_assessment:
        "{{name}} will no longer be able to complete the assessment you asked for.",
      read_shared_assessments_one:
        "{{name}} will no longer be able to open the assessment shared with them.",
      read_shared_assessments_other:
        "{{name}} will no longer be able to open the {{count}} assessments shared with them.",
    },
    cancel: "Cancel",
    confirm: "End access",
    failed: "Their access could not be ended. Try again.",
    ended: "{{name}}'s access has ended.",
  },

  // The guests on a person's Case involvement, and their Details.
  caseGuests: {
    title: "Guests",
    role: {
      read_shared_assessments: "Reads shared assessments",
      complete_assessment: "Completes an assessment",
    },
    column: {
      access: "Access",
    },
    access: {
      read_shared_assessments_one: "{{count}} assessment",
      read_shared_assessments_other: "{{count}} assessments",
      notSent: "1 assessment, not sent yet",
      sent: "1 assessment, sent",
    },
    ended: "Ended",
    details: "Details",
    detailsOf: "Details of {{name}}",
    loadFailed: "Could not load the guests.",
  },

  guestDetails: {
    email: "Email",
    sharedBy: "Shared by",
    askedBy: "Asked by",
    byOn: "{{name}} on {{date}}",
    until: "Access until",
    sendBy: "Send by",
    endedOn: "Access ended",
    hasAccessed: "Has accessed",
    accessed: "Yes, first on {{first}}, last on {{last}}",
    notYet: "Not yet",
    shared_one: "{{count}} assessment shared",
    shared_other: "{{count}} assessments shared",
    noneShared: "None of the assessments shared can be opened any more.",
    status: {
      approved: "Approved",
      completed: "Completed",
    },
    opened: "Opened {{date}}",
    notOpened: "Not opened",
    progress: "Progress",
    domainsAnswered: "{{completed}} of {{included}} domains answered",
    progressGone: "Their answers were not kept.",
    notSent: "Not sent yet",
    sent: "Sent {{date}}",
    openAssessment: "Open the assessment",
    message: "Message",
    noMessage: "No message was sent.",
    loading: "Loading their details",
    failed: "Their details could not be loaded.",
    close: "Close",
  },

  // Sharing finished assessments with somebody outside the service.
  shareWithGuest: {
    open: "Share outside the service",
    title: "Share outside the service",
    choose: {
      intro:
        "Choose the finished assessments of {{personName}} to share. You can share the ones you can read.",
      selectAll: "Select all",
      assessment: "{{kind}}, {{finished}}, by {{assessor}}",
      definitive: "Definitive",
      nonDefinitive: "Non-definitive",
      showOlder: "Show older",
      showFewer: "Show fewer",
      chosen_one: "{{count}} chosen",
      chosen_other: "{{count}} chosen",
      clear: "Clear",
      next: "Next",
      none: "There are no finished assessments you can share.",
    },
    who: {
      sharing_one: "Sharing {{count}} assessment, {{range}}",
      sharing_other: "Sharing {{count}} assessments, {{range}}",
      range: "from {{from}} to {{to}}",
      onOneDay: "from {{date}}",
      andMore: "and {{count}} more",
      showAll: "Show all",
      change: "Change",
      endsOn: "How long they can read them",
      endsAfterThreeMonths: "3 months, until {{date}}",
      endsOnChosenDay: "Until a date I choose",
      chosenDayHelp: "They can read them until the end of this day.",
      warning:
        "I have checked who this person is and their email address, and I understand they will be able to read and download what is shared about {{personName}}.",
      warningRequired: "Tick this to confirm before sharing.",
      send_one: "Share {{count}} assessment and send email",
      send_other: "Share {{count}} assessments and send email",
    },
    earlierGuest: {
      question:
        "{{email}} has been used for a guest before. Is this the same person?",
    },
    outcome: {
      already_shared:
        "This person can already read assessments shared about this young person. They can be shared with again once that access ends.",
      no_date_of_birth:
        "{{personName}}'s record has no date of birth, which a guest needs to sign in. Add it on the Identity tab first.",
      failed: "The assessments could not be shared. Try again.",
    },
    shared_one: "Shared {{count}} assessment with {{email}}.",
    shared_other: "Shared {{count}} assessments with {{email}}.",
  },

  // A guest signing in through the link they were emailed, and what they
  // see once in.
  guestAccess: {
    opening: "Opening your link",
    continue: "Continue",
    decline: "This wasn't meant for me",
    welcome: {
      heading: "Hello {{guest}}",
      complete_assessment:
        "{{sharer}} at {{organisation}} has asked you to complete an assessment of {{personFirstName}}'s needs.",
      read_shared_assessments:
        "{{sharer}} at {{organisation}} has shared assessments of {{personFirstName}}'s needs with you.",
      howItWorks:
        "To keep {{personFirstName}}'s information safe, we'll email you a code, then ask for their date of birth.",
      sendCode: "Email me a code",
    },
    code: {
      heading: "Enter the code we emailed you",
      intro: "It's 6 digits, and it works for 10 minutes.",
      label: "Code",
      sixDigits: "Enter the 6 digits from the email.",
      wrong:
        "That code doesn't match. You have {{count}} more tries before this link locks.",
      wrongLastTry:
        "That code doesn't match. You have 1 more try before this link locks.",
      expired: "This code has run out. Send a new code.",
      sentAMomentAgo:
        "We sent a code a moment ago. Wait a minute before asking for another.",
      sendAgain: "Send a new code",
    },
    dateOfBirth: {
      heading: "What is {{personFirstName}}'s date of birth?",
      intro: "For example, 27 3 2012.",
      day: "Day",
      month: "Month",
      year: "Year",
      invalid: "Enter a real date.",
      wrong:
        "That date doesn't match. You have {{count}} more tries before this link locks.",
      wrongLastTry:
        "That date doesn't match. You have 1 more try before this link locks.",
    },
    agreement: {
      heading: "Before you start",
      agree: "I agree",
    },
    closed: {
      contact:
        "If you have any questions, contact {{sharer}} at {{organisation}}.",
      locked: {
        heading: "This link has been locked",
        body: "There were too many wrong tries. We've let {{sharer}} know. They can send you a new link.",
      },
      ended: {
        heading: "Your access has ended",
        body: "This link no longer opens.",
      },
      expired: {
        heading: "Your access ended on {{date}}",
        body: "The date {{sharer}} gave you has passed, so this link no longer opens.",
      },
      sent: {
        heading: "You've already sent this assessment",
        body: "{{sharer}} has it. This link no longer opens.",
      },
      address_refused: {
        heading: "This link can no longer be used",
        body: "{{organisation}} no longer accepts guests from your email address.",
      },
    },
    declined: {
      heading: "Thanks for letting us know",
      body: "This link no longer works. We've let {{sharer}} know.",
    },
    noLink: {
      heading: "This link doesn't work",
      body: "Check you've used the whole link from your email. If it still doesn't work, ask whoever sent it for a new one.",
    },
    unavailable: {
      heading: "Something went wrong",
      body: "We couldn't reach Haven. Check your connection and try again.",
      retry: "Try again",
    },
    sessionEnded: {
      heading: "You've been signed out",
      body: "Open the link in your email again to carry on.",
    },
    signedOut: {
      heading: "You've signed out",
      body: "To come back, open the link in your email again.",
    },
  },

  // A guest completing the assessment they were asked for.
  guestAssessment: {
    heading: "Assessment of {{personFirstName}}'s needs",
    messageFrom: "Message from {{asker}}",
    send: "Send to {{asker}}",
    answered:
      "{{answered}} of {{asked}} answered. You can send with items left unanswered.",
    notReady: {
      commentsMissing:
        "Ratings of 2 and 3 need a comment before you can send. {{count}} missing.",
      nothingAnswered: "Answer at least one item before you can send.",
      answersToCheck: "Some answers need looking at again before you can send.",
    },
    askedBy: "Asked by",
    askerAt: "{{asker}}, {{organisation}}",
    sendBy: "Send by",
    notOpen: "This assessment has been sent.",
  },

  // A guest reading the assessments shared with them.
  guestShared: {
    heading: "Assessments of {{personFirstName}}'s needs",
    sharedBy: "Shared by {{sharer}}, {{organisation}}",
    readUntil: "You can read and download these until {{date}}.",
    definitiveExplained:
      "A definitive assessment is the one the young person's designated assessor completed and an approver approved. A non-definitive assessment is someone else's view, completed alongside it.",
    definitive: "Definitive",
    nonDefinitive: "Non-definitive",
    finished: {
      approved: "Approved {{date}}",
      completed: "Completed {{date}}",
    },
    by: "By {{assessor}}",
    unknownAssessor: "Assessor not known",
    headline: {
      needs_one: "{{count}} need to act on",
      needs_other: "{{count}} needs to act on",
      strengths_one: "{{count}} strength to build",
      strengths_other: "{{count}} strengths to build",
      history_one: "{{count}} item of history to know",
      history_other: "{{count}} items of history to know",
      caregiver_one: "{{count}} caregiver need to act on",
      caregiver_other: "{{count}} caregiver needs to act on",
    },
    open: "Open",
    openOf: "Open the assessment {{finished}}",
    download: "Download PDF",
    downloadOf: "Download the assessment {{finished}} as a PDF",
    downloadFailed: "The PDF couldn't be downloaded. Try again.",
    none: "There is nothing shared with you that you can still read.",
    loadFailed: "The shared assessments couldn't be read. Try again.",
    retry: "Try again",
    back: "Back to the shared assessments",
    notFound: "This assessment isn't shared with you.",
  },

  // The page a guest lands on once their assessment is sent.
  guestSent: {
    heading: "Thank you",
    sentTo: "Your assessment has been sent to {{asker}}.",
    sent: "Your assessment has been sent.",
    closed: "Your link no longer opens. You can close this page.",
    download: "Download a copy (PDF)",
    downloadOnce:
      "You won't be able to open this again once you leave this page.",
    downloadFailed: "The copy couldn't be downloaded. Try again.",
  },

  // Refusals from the request asking a guest, named by the shared schema.
  guest: {
    nameRequired: "Enter their name.",
    nameTooLong: "A name can be at most 200 characters.",
    messageTooLong: "A message can be at most 2000 characters.",
    endsOnInvalid: "Enter a real date.",
    endsOnPast: "Choose today or a later date.",
  },

  // A contributor's standing: who they are to the person, and their job.
  standing: {
    relation: {
      professional: {
        teacher: "Teacher",
        school_staff: "Other school staff",
        gp: "GP",
        health_professional: "Health professional",
        youth_justice_worker: "Youth justice worker",
        legal_representative: "Legal representative",
        advocate: "Advocate",
        independent_reviewing_officer: "Independent Reviewing Officer",
        other: "Someone else",
      },
    },
    relationDescriptionRequired: "Describe who they are.",
    relationDescriptionTooLong: "A description can be at most 200 characters.",
    relationDescriptionUnexpected: "Leave this empty.",
    jobTitleRequired: "Enter their job title.",
    jobTitleTooLong: "A job title can be at most 200 characters.",
  },

  // Diagnostics for a support conversation: the build someone is on, read
  // straight off the page.
  version: {
    client: "v: {{version}}",
    server: "api: {{version}}",
    mismatch:
      "The app and the API are running different versions. Reload to update.",
  },
} as const;

export default common;
